import { useMemo } from 'react';
import { Users } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import type { TeamMember } from '../types';

interface TeamAttendanceProps {
  team: TeamMember[];
  loading: boolean;
  isError: boolean;
}

function memberState(m: TeamMember): 'present' | 'checked_out' | 'absent' {
  if (m.check_out) return 'checked_out';
  if (m.check_in) return 'present';
  return 'absent';
}

export function TeamAttendance({ team, loading, isError }: TeamAttendanceProps) {
  const kpis = useMemo(() => {
    let present = 0;
    let checkedOut = 0;
    let notIn = 0;
    for (const m of team) {
      const s = memberState(m);
      if (s === 'present') present++;
      else if (s === 'checked_out') checkedOut++;
      else notIn++;
    }
    return { present, checkedOut, notIn };
  }, [team]);

  const columns: Column<TeamMember>[] = [
    {
      key: 'name',
      header: 'Employee',
      render: (m) => <span className="font-medium text-on-surface">{m.display_name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) => {
        const s = memberState(m);
        return (
          <Badge tone={s === 'present' ? 'success' : s === 'checked_out' ? 'info' : 'neutral'}>
            {s === 'present' ? 'Checked In' : s === 'checked_out' ? 'Checked Out' : 'Not In'}
          </Badge>
        );
      },
    },
    { key: 'check_in', header: 'Check In', render: (m) => m.check_in || '—' },
    { key: 'check_out', header: 'Check Out', render: (m) => m.check_out || '—' },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      render: (m) => (m.hours != null && m.hours > 0 ? `${m.hours}h` : '—'),
    },
  ];

  return (
    <Card>
      <div className="flex items-center gap-2 border-b border-outline px-5 py-4">
        <Users className="h-5 w-5 text-primary" />
        <h2 className="text-data-lg font-semibold text-on-surface">Team Attendance — Today</h2>
      </div>
      <div className="space-y-4 p-5">
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-body-sm text-on-surface-variant">Failed to load team attendance.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Kpi label="Present" value={kpis.present} tone="success" />
              <Kpi label="Checked Out" value={kpis.checkedOut} tone="info" />
              <Kpi label="Not Checked In" value={kpis.notIn} tone="neutral" />
            </div>
            <Table
              columns={columns}
              data={team}
              rowKey={(m) => m.id}
              empty="No team members found for your department."
            />
          </>
        )}
      </div>
    </Card>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone: 'success' | 'info' | 'neutral' }) {
  const toneCls =
    tone === 'success'
      ? 'text-success'
      : tone === 'info'
        ? 'text-info'
        : 'text-on-surface-variant';
  return (
    <div className="rounded-card-sm border border-outline bg-surface-container-low px-4 py-3 text-center">
      <p className={`text-display-sm font-semibold ${toneCls}`}>{value}</p>
      <p className="mt-1 text-body-sm text-on-surface-variant">{label}</p>
    </div>
  );
}
